/* eslint-disable @typescript-eslint/naming-convention */
import nativeBridge from '@ifit/native-webview-interface';
import React, { useContext, useState, useRef, useEffect } from 'react';
import {
  IEventOrChallengeOrMilestone,
  useQueryGetUserMilestones,
} from '@ifit/gateway-wolf-athenaeum-service';
import { useAchievementsQuery } from '../../../../client/query/achievements';
import { useConfig } from '../../../../store/reducers/config';
import { useDeviceType } from '../../../../store/device-type';
import { tabletSize } from '../../../../helpers/responsive';
import { useMembership } from '../../../../client/query/users';
import {
  milestonesYear,
  daysRemaining,
  milestonesWorkoutsCompleted,
  milestonesNextMilestone,
  hasNoCompletedMilestonesInCurrentYear,
  hasNoCompletedMilestonesInPastYears,
  currentYearMilestoneInfoPanel,
  pastYearMilestoneInfoPanel,
  pastYearMilestoneInfoPanelPost2025,
  completedByEndOfYear,
  browseWorkouts,
  upgradeFreeMilestonesAchievementCaption,
  milestonesTabTitle,
} from '../../../../i18n/translations';
import Loading from '@ifit/mp-loading';
import ProgressCircle from '@ifit/mp-progress-circle';
import GridList from '@ifit/mp-gridlist';
import Badge from '@ifit/mp-badge';
import InfoPanel from '@ifit/mp-info-panel';
import useInfoPanelPosition from '../../../../helpers/use-info-panel-positioning';
import { Link } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import Icon from '@ifit/react-icons/dist/components/icon';
import LOCKED_LINE from '@ifit/react-icons/dist/icons/LOCKED_LINE';
import CHEVRON_DOWN_LINE from '@ifit/react-icons/dist/icons/CHEVRON_DOWN_LINE';
import LONG_ARROW_RIGHT_LINE from '@ifit/react-icons/dist/icons/LONG_ARROW_RIGHT_LINE';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import {
  shark,
  white,
  scienceBlue,
  mountainMeadow,
} from '@ifit/base-coat/js/colors';
import styles from './index.module.scss';

export interface IMilestonesYearSelect {
  selectedYear: number;
  hasNoCompletedMilestones: boolean;
  handleYearChange: (value: number) => void;
  infoPanelContainer: HTMLElement;
}

export interface IWorkoutsCompleted {
  workoutsCompleted: number | undefined | null; // undefined = loading, null = error
  progress: number;
}

const callHome = () => {
  return (event: React.MouseEvent) => {
    nativeBridge.callNative({
      methodName: 'RunBehavior',
      key: 'NavigateToPath',
      path: 'library',
      respond: false,
    });
    event.preventDefault();
  };
};

export interface INextMilestone {
  hasCompletedAllMilestones: boolean;
  nextMilestoneNumber: number;
  nextMilestoneBadgeImage: { badgeImage: string; achievementId: number };
}

export interface IMilestonesGridList {
  milestoneBadges: [];
}

const COLORS = {
  LIGHT: {
    infoIconColor: shark,
    chevronIconColor: shark,
    lockedIconColor: shark,
    arrowIconColor: scienceBlue,
  },
  DARK: {
    infoIconColor: white,
    chevronIconColor: white,
    lockedIconColor: white,
    arrowIconColor: mountainMeadow,
  },
  VAULT: {
    infoIconColor: shark,
    chevronIconColor: shark,
    lockedIconColor: shark,
    arrowIconColor: scienceBlue,
  },
};

const years = (): number[] => {
  const currentYear = new Date().getFullYear();
  const lastYear = 2019;
  const yearList = [];
  for (let i = currentYear; i >= lastYear; i--) {
    yearList.push(i);
  }
  return yearList;
};

const useThemeColors = (icon: string): string => {
  const { theme } = useContext(ThemeContext);
  let iconColor;

  if (icon === 'INFO_CIRCLE_SOLID') {
    iconColor = COLORS[theme].infoIconColor;
  } else if (icon === 'CHEVRON_DOWN_LINE') {
    iconColor = COLORS[theme].chevronIconColor;
  } else if (icon === 'LONG_ARROW_RIGHT_LINE') {
    iconColor = COLORS[theme].arrowIconColor;
  } else if (icon === 'LOCKED_LINE') {
    iconColor = COLORS[theme].lockedIconColor;
  } else {
    iconColor = '#000000';
  }

  return iconColor;
};

const MilestonesYearSelect: React.FC<IMilestonesYearSelect> = ({
  selectedYear,
  hasNoCompletedMilestones,
  handleYearChange,
  infoPanelContainer,
}: IMilestonesYearSelect) => {
  const chevronIconColor = useThemeColors('CHEVRON_DOWN_LINE');

  const currentYear = new Date().getFullYear();

  const infoPanelText = (): string => {
    if (selectedYear === currentYear) {
      return currentYearMilestoneInfoPanel(selectedYear);
    } else if (selectedYear >= 2025) {
      return pastYearMilestoneInfoPanelPost2025(selectedYear);
    } else {
      return pastYearMilestoneInfoPanel(selectedYear);
    }
  };

  const infoPanelPosition = useInfoPanelPosition();

  const daysRemainingInYear = (): number => {
    const now = new Date();
    const lastDayOfYear = new Date(now.getFullYear(), 11, 31);
    const ONE_DAY = 1000 * 60 * 60 * 24;

    return Math.ceil(
      Math.abs(now.getTime() - lastDayOfYear.getTime()) / ONE_DAY,
    );
  };

  return (
    <div
      className={`${styles.milestonesYearSelect} ${styles.milestonesTextWrapper}`}
    >
      <Listbox value={selectedYear} onChange={handleYearChange}>
        <Listbox.Button className={styles.milestonesYearTitle}>
          {milestonesYear(selectedYear)}
          <Icon
            icon={CHEVRON_DOWN_LINE}
            color={chevronIconColor}
            size={20}
            className={styles.milestonesYearTitleChevron}
          />
        </Listbox.Button>
        <Listbox.Options className={styles.milestonesYearOptions}>
          {years().map((year, index) => (
            <Listbox.Option
              className={`${styles.milestonesYearOption} ${
                selectedYear === year ? styles.selected : ''
              }`}
              key={index}
              value={year}
            >
              {year}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>
      <div
        className={
          hasNoCompletedMilestones
            ? styles.hasNoCompletedMilestonesText
            : styles.milestonesBarText
        }
      >
        {selectedYear === currentYear
          ? `${daysRemaining()} ${daysRemainingInYear()}`
          : completedByEndOfYear()}
        <InfoPanel
          info={infoPanelText()}
          className={styles.infoPanel}
          container={infoPanelContainer}
          verticalPosition={infoPanelPosition}
        ></InfoPanel>
      </div>
    </div>
  );
};

const WorkoutsCompleted: React.FC<IWorkoutsCompleted> = ({
  workoutsCompleted,
  progress,
}: IWorkoutsCompleted) => {
  const deviceType = useDeviceType();
  const { isVault } = useConfig();
  let diameter;
  const [deviceSize, setDeviceSize] = useState(tabletSize(deviceType));
  const handleResize = (): void => {
    setDeviceSize(tabletSize(deviceType));
  };

  window.addEventListener('resize', handleResize);

  if (deviceType === 'mobile') {
    diameter = 60;
  } else if (isVault) {
    // don't set diameter
  } else if (deviceSize === 'isTwentyTwoInch') {
    diameter = 96;
  } else if (deviceSize === 'isTenInch') {
    diameter = 88;
  } else if (deviceSize === 'isSevenInch') {
    diameter = 72;
  }

  return (
    <div className={styles.workoutsCompleted}>
      {workoutsCompleted !== null && workoutsCompleted !== undefined ? (
        <>
          <ProgressCircle
            progress={progress}
            className={styles.milestonesProgressCircle}
            deviceType={deviceType}
            isVault={isVault}
            diameter={diameter}
          />
          <div className={styles.milestonesTextWrapper}>
            <div className={styles.milestonesBarText}>
              {`${milestonesWorkoutsCompleted()}`}
            </div>
            <div className={styles.milestonesBarNumber}>
              {workoutsCompleted}
            </div>
          </div>
        </>
      ) : (
        <span data-testid="workouts-completed-loading">
          <Loading absolute />
        </span>
      )}
    </div>
  );
};

const NextMilestone: React.FC<INextMilestone> = ({
  hasCompletedAllMilestones,
  nextMilestoneNumber,
  nextMilestoneBadgeImage,
}: INextMilestone) => (
  <div className={styles.nextMilestone}>
    <Badge
      data={nextMilestoneBadgeImage}
      referralSource={'Library'}
      imageOnly
    />
    {!hasCompletedAllMilestones && (
      <div className={styles.milestonesTextWrapper}>
        <div className={styles.milestonesBarText}>
          {milestonesNextMilestone()}
        </div>
        <div className={styles.milestonesBarNumber}>{nextMilestoneNumber}</div>
      </div>
    )}
  </div>
);

const MilestonesBadgeGridList: React.FC<IMilestonesGridList> = ({
  milestoneBadges,
}: IMilestonesGridList) => {
  const deviceType = useDeviceType();
  return (
    <div className={styles.milestonesGridList}>
      <GridList
        data={milestoneBadges}
        containsLibraryBadges
        minWidth={null}
        gridGap={3}
        deviceType={deviceType}
        className={styles.milestonesGridlist}
      >
        {milestoneBadge => milestoneBadge}
      </GridList>
    </div>
  );
};

const Milestones: React.FC = () => {
  const infoPanelContainerRef = useRef<HTMLDivElement>(undefined);
  let milestoneBadges;
  let nextMilestone;
  let currentMilestone;
  let hasNoCompletedMilestones;
  let hasCompletedAllMilestones;
  const arrowIconColor = useThemeColors('LONG_ARROW_RIGHT_LINE');
  const lockedIconColor = useThemeColors('LOCKED_LINE');
  const [year, setYear] = useState(years()[0]);
  const selectedYear = year;
  const currentYear = new Date().getFullYear();
  const { language } = useConfig();
  const { isFreeLibrary } = useMembership();

  function handleYearChange(value): void {
    setYear(value);
  }

  const { data, isLoading, isError } = useQueryGetUserMilestones({
    year: selectedYear,
  });
  const workoutsCompleted = isError
    ? null
    : isLoading
    ? undefined
    : data?.countYTD;
  const achievementsMilestones = useAchievementsQuery(
    IEventOrChallengeOrMilestone.Milestone,
    selectedYear,
  );
  const milestonesIsLoading = achievementsMilestones?.isLoading;
  const milestonesData = achievementsMilestones?.data?.data?.milestones;

  if (!milestonesIsLoading && milestonesData) {
    milestoneBadges = milestonesData.map((milestoneBadge, index) => {
      return (
        <Badge
          language={language}
          data={milestoneBadge}
          key={index}
          referralSource={'Library'}
          className={styles.milestoneBadge}
        />
      );
    });
    // check for all milestones completed
    hasCompletedAllMilestones =
      milestonesData.filter(milestoneBadge => {
        return milestoneBadge.completed === true;
      }).length === milestonesData.length
        ? true
        : false;
    // get first milestone with completed as false
    nextMilestone = milestonesData.find(milestoneBadge => {
      return milestoneBadge.completed === false;
    });
    // get last milestone with completed as true
    currentMilestone = milestonesData
      .filter(milestoneBadge => {
        return milestoneBadge.completed === true;
      })
      .slice(-1)[0];
    // check for no milestones
    hasNoCompletedMilestones =
      milestonesData.filter(milestoneBadge => {
        return milestoneBadge.completed === false;
      }).length === milestonesData.length
        ? true
        : false;
  }

  const nextMilestoneNumber: number = nextMilestone?.milestoneCount;
  const progress: number = hasCompletedAllMilestones
    ? 100
    : (workoutsCompleted / nextMilestoneNumber) * 100;
  const validProgress: number = isNaN(progress) ? 0 : progress;

  const [infoPanelContainer, setInfoPanelContainer] = useState(
    infoPanelContainerRef.current ?? undefined,
  );

  if (infoPanelContainer !== infoPanelContainerRef.current) {
    setInfoPanelContainer(infoPanelContainerRef.current);
  }

  useEffect(() => {
    if (!milestonesIsLoading) {
      setInfoPanelContainer(infoPanelContainerRef.current);
    }
  }, [milestonesIsLoading]);

  if (isFreeLibrary) {
    return (
      <div>
        <header>
          <div>
            <h1>{milestonesTabTitle()}</h1>
          </div>
        </header>
        <div className="free-user">
          <Icon
            icon={LOCKED_LINE}
            color={lockedIconColor}
            size={32}
            className="locked-line"
          />
          <p>{upgradeFreeMilestonesAchievementCaption()}</p>
        </div>
      </div>
    );
  }

  return !milestonesIsLoading ? (
    <>
      {hasNoCompletedMilestones ? (
        <div
          className={`${styles.hasNoCompletedMilestonesBanner} ${styles.milestonesTextWrapper}`}
          data-testid="hasNoCompletedMilestonesBanner"
          ref={infoPanelContainerRef}
        >
          <MilestonesYearSelect
            selectedYear={selectedYear}
            hasNoCompletedMilestones={hasNoCompletedMilestones}
            handleYearChange={handleYearChange}
            infoPanelContainer={infoPanelContainer}
          ></MilestonesYearSelect>
          <div
            className={`${styles.hasNoCompletedMilestonesText} ${styles.hasNoCompletedMilestonesMessage}`}
          >
            {currentYear === selectedYear
              ? hasNoCompletedMilestonesInCurrentYear(selectedYear)
              : hasNoCompletedMilestonesInPastYears(selectedYear)}
          </div>
          <Link
            to=""
            onClick={callHome()}
            className={styles.hasNoCompletedMilestonesLink}
          >
            {browseWorkouts()}
            <Icon
              icon={LONG_ARROW_RIGHT_LINE}
              color={arrowIconColor}
              size={20}
              className={styles.ifitReactIcon}
            />
          </Link>
        </div>
      ) : (
        <div ref={infoPanelContainerRef} className={styles.milestonesBar}>
          <MilestonesYearSelect
            selectedYear={selectedYear}
            hasNoCompletedMilestones={hasNoCompletedMilestones}
            handleYearChange={handleYearChange}
            infoPanelContainer={infoPanelContainer}
          ></MilestonesYearSelect>
          <WorkoutsCompleted
            workoutsCompleted={workoutsCompleted}
            progress={validProgress}
          ></WorkoutsCompleted>
          <NextMilestone
            hasCompletedAllMilestones={hasCompletedAllMilestones}
            nextMilestoneNumber={nextMilestoneNumber}
            nextMilestoneBadgeImage={currentMilestone}
          ></NextMilestone>
        </div>
      )}
      <MilestonesBadgeGridList
        milestoneBadges={milestoneBadges}
      ></MilestonesBadgeGridList>
    </>
  ) : (
    <span data-testid="milestones-loading">
      <Loading absolute />
    </span>
  );
};

export default withTheme(Milestones);
